import React, { useCallback } from "react";
import { observer } from "mobx-react";
import { ColorX, LocaleX } from "IZOX/STATIC";
// eslint-disable-next-line no-unused-vars
import PropsType from "prop-types";
import { HStack, Spacer, XButton, XDiv, XText } from "IZOX/_X";
import { QFDropdown } from "IZOX/BLOCKS/Libs/QForm/_gears";
import _ from "lodash";
import { QForm } from "IZOX/BLOCKS/Libs";
import { GoChevronLeft, GoChevronRight } from "react-icons/go";

/**
 * @typedef {Object} pageflipperparams
 * @property {number} page - Current page number, starting from 0.
 * @property {function} onPageChange - Callback function to handle page change.
 * @property {function} onPageSizeChange - Callback function to handle page size change.
 * @property {number} totalDocs - Total number of documents.
 * @property {number} pageSize - Number of documents per page.
 * @property {Array<number>} pageSizeOptions - Options for page size selection.
 * @property {string} popupPosition - Position of the dropdown popup.
 * @property {boolean} showPageSizeOption - Whether to show the page size selection option.
 * @property {string} bColor - Border color for the dropdown.
 * @property {string} mainColor - Main color for the buttons and text.
 * @property {string} mainDarkColor - Dark mode main color for the buttons and text.
 * @property {string} disableColor - Color for disabled buttons.
 * @property {string} disableDarkColor - Dark mode color for disabled buttons.
 * @property {boolean} darkMode - Whether dark mode is enabled.
 * @property {number} fontSize - Font size for the text. * 
 * 
 * @param {pageflipperparams} param0 
 * @returns 
 */
function PageFlipper({
  page = 0,
  onPageChange = null,
  onPageSizeChange = null,
  totalDocs = 1,
  pageSize = 5,
  pageSizeOptions = [5, 10, 20, 50],
  popupPosition = "bottom",
  showPageSizeOption = true,
  bColor = undefined,
  mainColor = ColorX.CSS("cmDarkBlue"),
  mainDarkColor = ColorX.CSS("dmWhite"),
  disableColor = ColorX.CSS("greyOut"),
  disableDarkColor = ColorX.CSS("greyOut"),
  darkMode = false,
  fontSize = 14
}){

  const _onPageSizeChange = useCallback(({pageSize}) => {
    onPageSizeChange?.(pageSize);
    onPageChange?.(0);
  }, [onPageSizeChange, onPageChange]);

  const _onPageChange = useCallback((page) => {
    onPageChange?.(page);
  }, [onPageChange]);

  const _getWidth = useCallback((count) => {
    return (50 + (String(count).length - 3) * 10) * fontSize / 16;
  }, [fontSize]);

  function renderPageSizeHandler(){
    let _options = _.map(pageSizeOptions, o => ({cap: String(o), val: o}));
    let color = darkMode ? mainDarkColor : mainColor;
    return (
      <HStack spacing={10} fitWidthToContent>
        <XText color={color} fontSize={fontSize}>
          {LocaleX.Parse({
            EN: "Items per page",
            TC: "每頁項目"
          })}
        </XText>
        <QForm value={{pageSize: pageSize}} onChange={_onPageSizeChange}>
          <XDiv round={50} bW={1} bColor={bColor ?? mainColor}>
            <QFDropdown name="pageSize" options={_options} 
              popupPosition={popupPosition ?? "bottom"}
              iconSize={10}
              height={fontSize * 2 - 1}
              arrowColor={color}
              color={color}
              fontSize={fontSize}
              // bgColor={ColorX.CSS(darkMode? "dmBgDark" : "transparent")}
              containerStyle={{
                backgroundColor: ColorX.CSS("transparent"),
                borderColor: color,
                borderWidth: 1,
                width: 80 * fontSize / 16,
                padding: 0,
                paddingLeft: 0,
                paddingRight: 5,
              }}
              />
          </XDiv>
        </QForm>
      </HStack>
    );
  }

  function renderLeftPages(totalPage){
    if(page === 0) return;
    if(page < 4 || totalPage < 7){
      return (
        <HStack fitWidthToContent spacing={5}>
          {_.map(_.range(page), o => {
            return renderPageNumber(o);
          })}
        </HStack>
      );
    }else if(page > totalPage - 5){
      return (
        <HStack fitWidthToContent spacing={5}>
          {renderPageNumber(0)}
          {renderDots()}
          {_.map(_.range(totalPage - 5, page), o => {
            return renderPageNumber(o);
          })}
        </HStack>
      );
    }else{
      return (
        <HStack fitWidthToContent spacing={5}>
          {renderPageNumber(0)}
          {renderDots()}
          {renderPageNumber(page - 1)}
        </HStack>
      );
    }
  }

  function renderRightPages(totalPage){
    if(totalPage < 7){
      return (
        <HStack fitWidthToContent spacing={5}>
          {_.map(_.range(page + 1, totalPage), o => {
            return renderPageNumber(o);
          })}
        </HStack>
      );
    }else if(page < 4){
      return (
        <HStack fitWidthToContent spacing={5}>
          {_.map(_.range(page + 1, 5), o => {
            return renderPageNumber(o);
          })}
          {renderDots()}
          {renderPageNumber(totalPage - 1)}
        </HStack>
      );
    }else if(page > totalPage - 5){
      return (
        <HStack fitWidthToContent spacing={5}>
          {_.map(_.range(page + 1, totalPage), o => {
            return renderPageNumber(o);
          })}
        </HStack>
      );
    }else{
      return (
        <HStack fitWidthToContent spacing={5}>
          {renderPageNumber(page + 1)}
          {renderDots()}
          {renderPageNumber(totalPage - 1)}
        </HStack>
      );
    }
  }

  function renderPageNumber(page){
    let dcolor = darkMode ? disableDarkColor : disableColor;
    return (
      <XButton width={_getWidth(page)} key={page} onPress={() => _onPageChange(page)}>
        <XText color={dcolor} textAlign="|" fontSize={fontSize}
          fontFamily="Red Hat Display">
          {String(page + 1)}
        </XText>
      </XButton>
    );
  }

  function renderDots(){
    let dcolor = darkMode ? disableDarkColor : disableColor;
    return (
      <HStack width={30}>
        <XText color={dcolor} textAlign="|" fontSize={fontSize}
          fontFamily="Red Hat Display">
            {"..."}
        </XText>
      </HStack>
    );
  }

  function renderPageHandler(){
    let totalPage = totalDocs > 0 ? Math.ceil(totalDocs / pageSize) : 1;
    let canLeft = page > 0;
    let canRight = page < totalPage - 1;
    let color = darkMode ? mainDarkColor : mainColor;
    let dcolor = darkMode ? disableDarkColor : disableColor;
    return (
      <HStack fitWidthToContent>
        <XButton disabled={!canLeft} onPress={() => _onPageChange(page - 1)}>
          <GoChevronLeft color={canLeft? color : dcolor} size={fontSize}/>
        </XButton>
        {renderLeftPages(totalPage)}
        <HStack width={_getWidth(page + 1)}>
          <XText color={color} textAlign="|" fontSize={fontSize}
            fontFamily="Red Hat Display"
            style={{
              paddingLeft: 3,
              paddingRight: 3,
              borderBottomWidth: 2,
              borderBottomStyle: "solid",
              borderColor: color
            }}>
            {page + 1}
          </XText>
        </HStack>
        {renderRightPages(totalPage)}
        <XButton disabled={!canRight} onPress={() => _onPageChange(page + 1)}>
          <GoChevronRight color={canRight? color : dcolor} size={fontSize}/>
        </XButton>
      </HStack>
    );
  }

  return (
    <HStack spacing={10} flexGrow={1}>
      {showPageSizeOption && renderPageSizeHandler()}
      <Spacer/>
      {renderPageHandler()}
    </HStack>
  );
}

export default observer(PageFlipper);
